import styles from './Tooltip.module.scss';
import { Portal } from '@components/Portal';
import { CSSTransition } from "react-transition-group";
import parse from 'html-react-parser';
import cn from "classnames";
import { useRef, useEffect, useLayoutEffect, useState } from "react";

export interface ITooltipProps {
  triggerRef?: React.LegacyRef<HTMLElement>;
  position?: "top" | "left" | "right" | "bottom";
  align?: "start" | "center" | "end";
  bottom?: number;
  left?: number;
  right?: number;
  text: React.ReactNode;
  top?: number;
  whiteSpace?: string;
  disabled?: boolean;
  maxWidth?: number;
}

// const positions = {
//   top: { bottom: "calc(100% + 2px)", right: "calc(50% + 8px)", transform: "translateX(calc(50% + 12px))" },
//   bottom: { top: "calc(100% + 2px)", right: "calc(50% + 8px)", transform: "translateX(calc(50% + 12px))" },
//   left: { bottom: "calc(50% + 8px)", right: "calc(100% + 2px)", transform: "translateY(calc(50% + 12px))" },
//   right: { bottom: "calc(50% + 8px)", left: "calc(100% + 2px)", transform: "translateY(calc(50% + 12px))" },
// }

const Tooltip: React.FC<ITooltipProps> = (props: ITooltipProps): React.ReactElement => {
  const { position, align, triggerRef, text, whiteSpace, disabled, maxWidth } = props;
  // const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isFocused = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [positionObj, setPositionObj] = useState({});

  // ВАЖНО: ref читаем только в момент использования (эффекты/рендер тултипа), а не при первом
  // рендере компонента — там triggerRef.current ещё null, и слушатели наведения не навесились бы.
  const getTrigger = (): HTMLElement | null =>
    (triggerRef && typeof triggerRef !== "string" && "current" in triggerRef && triggerRef.current) || null;

  const getPosition = () => {
    const trigger = getTrigger();
    if (!trigger) return {};
    const rect = trigger.getBoundingClientRect();
    const W = document.documentElement.clientWidth;
    const H = document.documentElement.clientHeight;

    if ([props.left, props.right, props.top, props.bottom].find(v => (v !== undefined))) {
      return {
        left: props.left === undefined ? undefined : `calc(${window.scrollX}px + ${rect.left}px + ${window.scrollX}px + ${props.left}px)`,
        right: props.right === undefined ? undefined : `calc(${W}px - ${window.scrollX}px - ${rect.right}px + ${props.right}px)`,
        top: props.top === undefined ? undefined : `calc(${rect.top}px + ${window.scrollY}px + ${props.top}px)`,
        bottom: props.bottom === undefined ? undefined : `calc(${H}px - ${rect.bottom}px - ${window.scrollY}px + ${props.bottom}px)`,
      }
    }

    const margin = '0px';
    const arrowHalfWidth = '3px';

    const getHorAlign = () => {
      if (align === "start") return {
        left: `calc(${rect.left}px + ${window.scrollX}px + ${rect.width / 2}px)`,
        transform: `translateX(calc(-100% - 4px + ${styles.alignSize} + ${arrowHalfWidth} + 3px))`,
      }
      if (align === "end") return {
        left: `calc(${rect.left}px + ${window.scrollX}px + ${rect.width / 2}px)`,
        transform: `translateX(calc(-4px - ${styles.alignSize} - ${arrowHalfWidth} - 3px))`,
      }
      return {
        left: `calc(${rect.left}px + ${window.scrollX}px + ${rect.width / 2}px)`,
        transform: "translateX(calc(-50% - 5px))",
      }
    }

    if (position === "right") {
      return {
        top: `calc(${rect.top}px + ${window.scrollY}px + ${rect.height / 2}px)`,
        left: `calc(${rect.right}px + ${margin})`,
        transform: "translateY(calc(-50% - 5px))",
      };
    }
    if (position === "left") {
      return {
        top: `calc(${rect.top}px + ${window.scrollY}px + ${rect.height / 2}px)`,
        right: `calc(${W}px - ${window.scrollX}px - ${rect.left}px + ${margin})`,
        transform: "translateY(calc(-50% - 5px))",
      };
    }
    if (position === "bottom") {
      return {
        top: `calc(${rect.bottom}px + ${window.scrollY}px + ${margin})`,
        ...getHorAlign()
      };
    }
    if (position === "top") {
      return {
        bottom: `calc(${H}px - ${rect.top}px - ${window.scrollY}px + ${margin})`,
        ...getHorAlign()
      };
    }
    return {}
  }

  const handlePositionChange = () => {
    setPositionObj(getPosition());
  }

  useEffect(() => {
    document.addEventListener('scroll', handlePositionChange, true);

    return () => {
      document.removeEventListener('scroll', handlePositionChange, true);
    }
  }, [])

  useLayoutEffect(() => {
    if (getTrigger()) {
      handlePositionChange();
    }
  }, [isOpen])

  useEffect(() => {
    const trigger = getTrigger();
    if (!trigger) return;

    const handleMouseLeave = () => {
      isFocused.current = false;
      setTimeout(() => {
        if (!isFocused.current) setIsOpen(false)
      }, 200);
    }
    const handleMouseEnter = () => {
      isFocused.current = true;
      setIsOpen(true);
    }

    trigger.addEventListener('mouseenter', handleMouseEnter);
    trigger.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      trigger.removeEventListener('mouseenter', handleMouseEnter);
      trigger.removeEventListener('mouseleave', handleMouseLeave);
    }
  }, [])

  // Триггер может лежать в порталенной выпадашке, открытой из модалки, — тогда самого
  // .modal в предках нет, но подняться над модалкой всё равно нужно (data-from-modal)
  const fromModal = getTrigger()?.closest(".modal, [data-from-modal]");

  if (!text || disabled) return (<></>)

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames={{
          enter: styles.tooltipEnter,
          enterActive: styles.tooltipEnterActive,
          exit: styles.tooltipExit,
          exitActive: styles.tooltipExitActive,
        }}
        mountOnEnter
        unmountOnExit
        nodeRef={tooltipRef}
      >
        <div
          ref={tooltipRef}
          className={cn(
            styles.tooltip,
            fromModal && styles.tooltipModal,
            styles[position ?? "top"],
            styles[align ?? "center"],
          )}
          style={{
            ...positionObj,
            whiteSpace: whiteSpace,
            maxWidth: `${maxWidth}px`,
          }}
        >
          <div className={styles.arrow} />
          {typeof text === "string" ? parse(text) : text}
        </div>
      </CSSTransition>
    </Portal>
  );
};

export default Tooltip;
